import React, { useEffect, useState } from 'react';
import { API_URL } from '../../config.ts';
import { DEFAULT_SETTINGS, SiteSettings, useSiteSettings } from '../../context/SiteSettingsContext.tsx';

const inputClass =
  'w-full px-3.5 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition';

const SiteSettingsForm: React.FC = () => {
  const { reload } = useSiteSettings();
  const [form, setForm] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    fetch(`${API_URL}/site-settings`)
      .then((res) => res.json())
      .then((data) => setForm({ ...DEFAULT_SETTINGS, ...data }))
      .catch(() => setMessage({ ok: false, text: 'Не удалось загрузить текущие значения' }))
      .finally(() => setLoaded(true));
  }, []);

  const set = (field: keyof SiteSettings) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [field]: e.target.value });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/site-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setForm({ ...DEFAULT_SETTINGS, ...(await res.json()) });
        await reload();
        setMessage({ ok: true, text: 'Сохранено — на сайте уже обновилось' });
      } else if (res.status === 401 || res.status === 403) {
        setMessage({ ok: false, text: 'Сессия истекла — войдите в админку заново' });
      } else {
        const body = await res.json().catch(() => null);
        setMessage({ ok: false, text: body?.message || 'Не удалось сохранить' });
      }
    } catch {
      setMessage({ ok: false, text: 'Нет связи с сервером' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 mb-8">
      <div className="px-6 py-5 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">Контакты сайта</h2>
        <p className="text-sm text-gray-500 mt-1">
          Показываются на главной, в «Контактах», «О себе» и в подвале. Пустое поле — блок скрывается.
        </p>
      </div>

      <div className="p-6 grid md:grid-cols-2 gap-5">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1.5">Местоположение</span>
          <input className={inputClass} value={form.location} onChange={set('location')} maxLength={200} disabled={!loaded} />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1.5">Телефон</span>
          <input className={inputClass} value={form.phone} onChange={set('phone')} maxLength={50} disabled={!loaded} />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1.5">Email</span>
          <input className={inputClass} value={form.email} onChange={set('email')} maxLength={200} placeholder="пусто — на сайте не показывается" disabled={!loaded} />
          <span className="block text-xs text-gray-400 mt-1">Выводится простым текстом, без ссылки (mailto).</span>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1.5">GitHub</span>
          <input className={inputClass} value={form.githubUrl} onChange={set('githubUrl')} maxLength={300} placeholder="https://github.com/..." disabled={!loaded} />
        </label>
        <label className="block md:col-span-2">
          <span className="block text-sm font-medium text-gray-700 mb-1.5">YouTube</span>
          <input className={inputClass} value={form.youtubeUrl} onChange={set('youtubeUrl')} maxLength={300} placeholder="https://youtube.com/..." disabled={!loaded} />
        </label>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-4">
        <button
          type="submit"
          disabled={saving || !loaded}
          className="bg-indigo-600 text-white text-sm font-medium px-5 py-2.5 rounded-lg shadow-sm hover:bg-indigo-700 transition disabled:opacity-50"
        >
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
        {message && (
          <span className={`text-sm ${message.ok ? 'text-green-600' : 'text-red-600'}`}>{message.text}</span>
        )}
      </div>
    </form>
  );
};

export default SiteSettingsForm;
